import { Children, isValidElement, type ElementType, type ReactNode } from 'react'
import { useInView } from '@/hooks/useInView'
import { cn } from '@/lib/cn'

type Variant = 'up' | 'down' | 'left' | 'right' | 'scale' | 'fade' | 'blur'

const HIDDEN: Record<Variant, string> = {
  up: 'translate-y-8 opacity-0',
  down: '-translate-y-8 opacity-0',
  left: 'translate-x-10 opacity-0',
  right: '-translate-x-10 opacity-0',
  scale: 'scale-[0.94] opacity-0',
  fade: 'opacity-0',
  blur: 'opacity-0 blur-[6px]',
}

const SHOWN = 'translate-x-0 translate-y-0 scale-100 opacity-100 blur-0'

/**
 * Reveals its children when they scroll into view.
 *
 * Motion is decorative throughout: `useInView` reports visible immediately
 * under `prefers-reduced-motion`, so the content simply appears.
 */
export function Reveal({
  children,
  variant = 'up',
  delay = 0,
  duration = 700,
  as: Tag = 'div',
  className,
}: {
  children: ReactNode
  variant?: Variant
  /** Milliseconds. */
  delay?: number
  duration?: number
  as?: ElementType
  className?: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()

  return (
    <Tag
      ref={ref}
      className={cn(
        'will-change-[transform,opacity] motion-safe:transition-all motion-safe:ease-brand',
        inView ? SHOWN : HIDDEN[variant],
        className,
      )}
      style={{ transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  )
}

/**
 * Reveals each child in sequence. One observer for the group rather than one
 * per item, so a long list does not create dozens of observers.
 */
export function Stagger({
  children,
  variant = 'up',
  step = 70,
  initialDelay = 0,
  duration = 650,
  as: Tag = 'div',
  itemAs: ItemTag = 'div',
  className,
  itemClassName,
}: {
  children: ReactNode
  variant?: Variant
  /** Milliseconds between each child. */
  step?: number
  initialDelay?: number
  duration?: number
  as?: ElementType
  itemAs?: ElementType
  className?: string
  itemClassName?: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const items = Children.toArray(children).filter(isValidElement)

  return (
    <Tag ref={ref} className={className}>
      {items.map((child, index) => (
        <ItemTag
          key={child.key ?? index}
          className={cn(
            'will-change-[transform,opacity] motion-safe:transition-all motion-safe:ease-brand',
            inView ? SHOWN : HIDDEN[variant],
            itemClassName,
          )}
          style={{
            transitionDuration: `${duration}ms`,
            transitionDelay: `${initialDelay + index * step}ms`,
          }}
        >
          {child}
        </ItemTag>
      ))}
    </Tag>
  )
}
